import { filter, find, intersection } from "lodash/fp";

import type { CalendarPermissionLevel } from "@app/analytics/types";
import type {
  ParticipantAccessLevel,
  User,
  UserDistributionSetting,
  UserSettings,
} from "@app/common/types";

import * as UserUtils from "./userUtils";

/** Simple helper function for getting the disabled state and correct tooltip
 * for the disabled state. Assumes that any disabled states are due to
 * Workspace restrictions */
export const getSettingDisabledOpts = ({
  settingIsEditable = true,
  optionIsSelectable = true,
  custom,
}: {
  settingIsEditable?: boolean;
  optionIsSelectable?: boolean;
  custom?: {
    /** Condition where, if true, setting is disabled */
    condition: boolean;
    /** Tooltip to show if the condition is false */
    tooltip: string;
  };
}): { disabled: boolean; tooltip?: string } => {
  if (!settingIsEditable) {
    return {
      disabled: true,
      tooltip: "Enforced by your Workspace",
    };
  }

  if (!optionIsSelectable) {
    return {
      disabled: true,
      tooltip: "Not available for your Workspace",
    };
  }

  if (custom?.condition) {
    return {
      disabled: true,
      tooltip: custom.tooltip,
    };
  }

  return { disabled: false };
};

const ALL_CALENDAR_PERMISSION_LEVELS: CalendarPermissionLevel[] = [
  "all",
  "hosting_only",
  "invite_only",
];

export const enabledCalendarPermissionLevelsForUser = (user: User | null) => {
  if (!user) return [];

  if (user.editOptions?.calendarPermissionLevel) {
    // do an intersection to preserve the order
    return intersection(
      user.editOptions.calendarPermissionLevel,
      ALL_CALENDAR_PERMISSION_LEVELS
    );
  }
  return ALL_CALENDAR_PERMISSION_LEVELS;
};

export const canEditCalendarJoinInternal = (user: User | null) =>
  UserUtils.hasCompanyEmail(user);

const ALL_PARTICIPANT_ACCESS_LEVELS: ParticipantAccessLevel[] = [
  "editor",
  "viewer_full",
  "no_access",
];

export const canEditInternalParticipantAccessLevel = (user: User | null) =>
  UserUtils.hasCompanyEmail(user);

export const enabledInternalParticipantAccessLevelsForUser = (
  user: User | null
): ParticipantAccessLevel[] => {
  if (canEditInternalParticipantAccessLevel(user)) {
    // do an intersection to preserve the order
    if (user?.editOptions?.internalParticipantAccessLevel) {
      return intersection(
        user.editOptions.internalParticipantAccessLevel,
        ALL_PARTICIPANT_ACCESS_LEVELS
      );
    }
    return ALL_PARTICIPANT_ACCESS_LEVELS;
  }
  return [];
};

export const enabledExternalParticipantAccessLevelsForUser = (
  user: User | null
): ParticipantAccessLevel[] => {
  if (!user) return [];

  // do an intersection to preserve the order
  if (user.editOptions?.externalParticipantAccessLevel) {
    return intersection(
      user.editOptions.externalParticipantAccessLevel,
      ALL_PARTICIPANT_ACCESS_LEVELS
    );
  }
  return ALL_PARTICIPANT_ACCESS_LEVELS;
};

/** Returns all existing user distribution settings for all providers for
 * the given category */
export const getUserDistributionSettingsForCategory = (
  userSettings: UserSettings,
  category: UserDistributionSetting["category"]
): UserDistributionSetting[] => {
  return filter({ category }, userSettings.userDistributionSettings);
};

/** Returns the existing user distribution settings for the category and
 * provider. Setting may be disabled */
export const getUserDistributionSettingForCategoryAndProvider = (
  userSettings: UserSettings | null,
  opts: {
    category: UserDistributionSetting["category"];
    provider: UserDistributionSetting["method"];
  }
): UserDistributionSetting | undefined => {
  return find(
    { category: opts.category, method: opts.provider },
    userSettings?.userDistributionSettings
  );
};

/** Returns the existing user distribution settings for the category and
 * provider, if enabled */
export const getEnabledUserDistributionSettingForCategoryAndProvider = (
  userSettings: UserSettings | null,
  opts: {
    category: UserDistributionSetting["category"];
    provider: UserDistributionSetting["method"];
  }
): UserDistributionSetting | undefined => {
  return find(
    { category: opts.category, method: opts.provider, isEnabled: true },
    userSettings?.userDistributionSettings
  );
};
